import React from 'react';
import { NullState, Sidebar, SidebarSection, toastSuccess } from 'bench-internal-theme';
import { useQuery } from '@apollo/client';
import { WrappedSpinner } from 'spinner';
import AddonSidebarFormContainer from './AddonSidebarFormContainer';
import { GET_ADDON } from '../queries';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@material-ui/core';
import { MicroFrontend, subscribe } from 'micro-frontend';
import { useClient } from '../../ClientContext';
import { AddOn } from 'general-ledger/types';
import Icon from 'primitive-components/Icon';
import { useStyles } from './AddonSidebar.styles';
import { FeatureValue, useFeatureFlags } from '@bench/ff-client';
import { SplitFeatureFlags } from 'bk-web-app/features';
import { SetupBusinessServices } from './components/setup-business-services/SetupBusinessServices';

type AddonSidebarProps = {
  id?: string;
  closeSidebar: () => void;
};

export type CancelBusinessServiceModalContent = {
  cancelMessage: string;
  confirmMessage: string;
  message: string;
  title: string;
};

const AddonSidebar = ({ id, closeSidebar }: AddonSidebarProps) => {
  const classes = useStyles();
  const client = useClient();
  const [
    {
      featureContext: { features },
    },
  ] = useFeatureFlags();
  const isDeprecateBenchServicesMfesOn =
    features[SplitFeatureFlags.DEPRECATE_BENCH_SERVICES_MFES]?.value === FeatureValue.FEATURE_ON;
  const [showCancelModal, setShowCancelModal] = React.useState(false);
  const [addOnData, setAddOnData] = React.useState<{ addon: AddOn } | null>(null);
  const [showSetupBusinessService, setShowSetupBusinessService] = React.useState(false);

  const handleDismissSidebar = () => {
    if (!showCancelModal) {
      setShowCancelModal(true);
      return;
    }
    closeSidebar();
  };

  const handleCancelModalClose = () => {
    setShowCancelModal(false);
  };

  const handleConfirmCancel = () => {
    setShowCancelModal(false);
    closeSidebar();
  };

  const handleCancelMessage = React.useCallback(() => {
    setShowCancelModal(true);
  }, [setShowCancelModal]);

  const handleSuccessMessage = React.useCallback(
    message => {
      toastSuccess(message?.title || 'Success', message?.message || 'Updated successfully.');
      closeSidebar();
    },
    [closeSidebar],
  );

  React.useEffect(() => {
    if (addOnData?.addon && !showSetupBusinessService) {
      setShowSetupBusinessService(true);
    }
  }, [addOnData, showSetupBusinessService]);

  React.useEffect(() => {
    // Only subscribe to MFE events when using MFE (feature flag OFF)
    if (!isDeprecateBenchServicesMfesOn) {
      const unsubscribeCancel = subscribe('business-service-setup-cancelled', handleCancelMessage);
      const unsubscribeSuccess = subscribe(
        'business-service-setup-succeeded',
        handleSuccessMessage,
      );
      return () => {
        unsubscribeCancel();
        unsubscribeSuccess();
      };
    }
  }, [handleCancelMessage, handleSuccessMessage, isDeprecateBenchServicesMfesOn]);

  const { loading, error, data } = useQuery(GET_ADDON, {
    variables: { addonId: id },
    skip: !id,
  });

  if (id && error) {
    return (
      <NullState
        bigEmoji="🙈"
        description={error.message}
        title="Uh oh! The following error was encountered while fetching the add-on for this sidebar."
      />
    );
  }
  if (id && loading) {
    return <WrappedSpinner />;
  }
  const addon = (data && data.addon) || undefined;
  const displaySetupBusinessServiceMFE = showSetupBusinessService;

  return (
    <Sidebar
      dismiss={handleDismissSidebar}
      headerContent={`${addon ? 'Edit' : 'Create'} Add-on${
        displaySetupBusinessServiceMFE ? ' (Step 2)' : ''
      }`}
    >
      <SidebarSection padding={showSetupBusinessService ? '0px 24px' : '24px'}>
        {displaySetupBusinessServiceMFE ? (
          isDeprecateBenchServicesMfesOn && addOnData?.addon ? (
            <SetupBusinessServices
              addOn={
                addOnData.addon as AddOn & {
                  accountType: string;
                  dateActive: string;
                  dateInactive: string;
                }
              }
              businessId={client.id}
              onCancel={handleCancelMessage}
              onSuccess={handleSuccessMessage}
            />
          ) : (
            <MicroFrontend
              name="SetupBusinessServices"
              data={{ addOn: addOnData?.addon, businessId: client.id }}
            />
          )
        ) : (
          <AddonSidebarFormContainer
            addon={addon}
            closeSidebar={closeSidebar}
            setAddOnData={setAddOnData}
          />
        )}
        <Dialog
          open={showCancelModal}
          onClose={handleCancelModalClose}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          className={classes.dialog}
        >
          <DialogTitle disableTypography className={classes.dialogTitle}>
            Unsaved Changes
          </DialogTitle>
          <DialogContent className={classes.dialogContent}>
            <p style={{ margin: '0' }}>
              You have unsaved changes. Closing this drawer will result the following:
            </p>
            <ul className={classes.list}>
              <li className={classes.listItem}>Services for this add-on will not be saved</li>
              <li className={classes.listItem}>Tasks will not be generated for this add-on</li>
            </ul>
            <p>Are you sure you want to proceed?</p>
            <div className={classes.alert}>
              <div>
                <Icon icon="exclamation" className={classes.alertIcon} />
              </div>
              <p className={classes.alertText}>
                If you no longer need to track this service, please <strong>delete</strong> the
                add-on you created in the last step.
              </p>
            </div>
          </DialogContent>
          <DialogActions className={classes.dialogActions}>
            <Button onClick={handleCancelModalClose} className={classes.dialogActionButton}>
              Cancel
            </Button>
            <Button onClick={handleConfirmCancel} autoFocus className={classes.dialogActionButton}>
              Close without Saving
            </Button>
          </DialogActions>
        </Dialog>
      </SidebarSection>
    </Sidebar>
  );
};
AddonSidebar.defaultProps = {
  id: undefined,
};

export default AddonSidebar;
